import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { InlineEdit } from "@/components/design/InlineEdit";
import { hasDuplicateName } from "@/lib/duplicate-name";

/**
 * The notebook's name, renamed where it stands, as a match's name is (Marvin, 2026-09-29): Enter or leaving the
 * field renames, Escape keeps the old name. The naming dialog's rules hold here too: an empty name or one another
 * notebook already has is not taken, and the field returns to the old name.
 */
export function StudioNotebookTitle({ name, otherNames, editable, onRename }: {
  name: string;
  otherNames: readonly string[];
  editable: boolean;
  onRename: (name: string) => Promise<boolean>;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(name);
  const duplicate = hasDuplicateName(otherNames, draft, name);
  const commit = async (committed: string) => {
    const next = committed.trim();
    if (!next || next === name || hasDuplicateName(otherNames, next, name)) {
      setDraft(name);
      return;
    }
    if (!(await onRename(next))) setDraft(name);
  };
  return (
    <>
      <h1 className="min-w-0">
        <InlineEdit
          id="studio-rename"
          wrap
          clearable={false}
          editable={editable}
          value={draft}
          onChange={setDraft}
          onCommit={(committed) => void commit(committed)}
          ariaLabel={intl.formatMessage({ id: "studio.renameNotebook", defaultMessage: "Rename notebook" })}
        />
      </h1>
      {duplicate ? (
        <p id="studio-rename-duplicate" className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="common.duplicateName" defaultMessage="An entry with this name already exists. Please choose a different name." />
        </p>
      ) : null}
    </>
  );
}
